import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import DetailPopup from "../../../components/DetailPopup/DetailPopup";
import Map from "../../../components/Map/Map";
import { IletisimService } from "../../../services/iletisim.service";
import { IletisimBilgileri, IletisimForm } from "../../../services/types";

const validationSchema = Yup.object().shape({
  eposta: Yup.string()
    .email("Geçerli bir e-posta adresi giriniz")
    .required("E-posta alanı zorunludur"),
  instagram: Yup.string(),
  twitter: Yup.string(),
  youtube: Yup.string(),
  linkedin: Yup.string(),
  facebook: Yup.string(),
  adres: Yup.string().required("Adres alanı zorunludur"),
  haritaUrl: Yup.string().required("Harita URL alanı zorunludur"),
});

const IletisimYonetimi: React.FC = () => {
  const [iletisimBilgileri, setIletisimBilgileri] =
    useState<IletisimBilgileri | null>(null);
  const [visible, setVisible] = useState(false);
  const [mesajlar, setMesajlar] = useState<IletisimForm[]>([]);
  const [loading, setLoading] = useState(false);
  const toast = useRef<Toast>(null);

  const [formData, setFormData] = useState({
    id: "",
    eposta: "",
    instagram: "",
    twitter: "",
    youtube: "",
    linkedin: "",
    facebook: "",
    adres: "",
    haritaUrl: "",
  });

  const [detailPopupVisible, setDetailPopupVisible] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState<Record<string, string>>(
    {}
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // İletişim bilgilerini getir
      const bilgilerResponse = await IletisimService.iletisimBilgileriGetir();
      if (bilgilerResponse.data && bilgilerResponse.data.length > 0) {
        const bilgi = bilgilerResponse.data[0];
        setIletisimBilgileri(bilgi);
        // Form verilerini mevcut bilgilerle doldur
        setFormData({
          id: bilgi.id,
          eposta: bilgi.eposta || "",
          instagram: bilgi.instagram || "",
          twitter: bilgi.twitter || "",
          youtube: bilgi.youtube || "",
          linkedin: bilgi.linkedin || "",
          facebook: bilgi.facebook || "",
          adres: bilgi.adres || "",
          haritaUrl: bilgi.haritaUrl || "",
        });
      }

      // Mesajları getir
      const mesajlarResponse = await IletisimService.iletisimFormListele();
      if (mesajlarResponse.data) {
        setMesajlar(mesajlarResponse.data);
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Veriler yüklenirken bir hata oluştu",
      });
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });

      await IletisimService.iletisimBilgileriGuncelle(formData);
      toast.current?.show({
        severity: "success",
        summary: "Başarılı",
        detail: "İletişim bilgileri başarıyla güncellendi",
      });
      loadData();
      setVisible(false);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        error.inner.forEach((err) => {
          toast.current?.show({
            severity: "error",
            summary: "Doğrulama Hatası",
            detail: err.message,
          });
        });
        return;
      }
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Güncelleme sırasında bir hata oluştu",
      });
    }
  };

  const confirmDelete = (item: IletisimForm) => {
    confirmDialog({
      message: "Bu mesajı silmek istediğinize emin misiniz?",
      header: "Silme Onayı",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Evet",
      rejectLabel: "Hayır",
      accept: () => handleDelete(item.id),
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await IletisimService.iletisimFormSil(id);
      toast.current?.show({
        severity: "success",
        summary: "Başarılı",
        detail: "Mesaj başarıyla silindi",
      });
      loadData();
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Silme işlemi sırasında bir hata oluştu",
      });
    }
  };

  const showDetailPopup = (rowData: IletisimForm) => {
    setSelectedDetail({
      "Ad Soyad": rowData.adSoyad,
      "Telefon No": rowData.telefonNo,
      Başlık: rowData.baslik,
      Mesaj: rowData.aciklama,
      Tarih: new Date(rowData.createdDate).toLocaleDateString(),
    });
    setDetailPopupVisible(true);
  };

  return (
    <div className="card p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="mb-5">
        <div className="flex align-items-center justify-content-between">
          <h2>İletişim Bilgileri</h2>
          <Button
            label="Güncelle"
            icon="pi pi-pencil"
            onClick={() => setVisible(true)}
          />
        </div>

        <div className="grid mt-3">
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="font-bold block mb-2">E-posta</label>
              <div className="text-900">{iletisimBilgileri?.eposta}</div>
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="font-bold block mb-2">Instagram</label>
              <div className="text-900">{iletisimBilgileri?.instagram}</div>
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="font-bold block mb-2">Twitter</label>
              <div className="text-900">{iletisimBilgileri?.twitter}</div>
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="font-bold block mb-2">Youtube</label>
              <div className="text-900">{iletisimBilgileri?.youtube}</div>
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="font-bold block mb-2">LinkedIn</label>
              <div className="text-900">{iletisimBilgileri?.linkedin}</div>
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="font-bold block mb-2">Facebook</label>
              <div className="text-900">{iletisimBilgileri?.facebook}</div>
            </div>
          </div>
          <div className="col-12">
            <div className="field">
              <label className="font-bold block mb-2">Adres</label>
              <div className="text-900">{iletisimBilgileri?.adres}</div>
            </div>
          </div>
          <div className="col-12">
            <div className="field">
              <label className="font-bold block mb-2">Harita</label>
              <Map url={iletisimBilgileri?.haritaUrl || ""} />
            </div>
          </div>
        </div>

        <Dialog
          baseZIndex={100}
          header="İletişim Bilgilerini Güncelle"
          visible={visible}
          style={{ width: "50vw" }}
          onHide={() => setVisible(false)}
          footer={
            <div>
              <Button
                label="İptal"
                icon="pi pi-times"
                onClick={() => setVisible(false)}
                className="p-button-text"
              />
              <Button
                label="Kaydet"
                icon="pi pi-check"
                onClick={handleSubmit}
                autoFocus
              />
            </div>
          }
        >
          <div className="grid ">
            <div className="col-12 md:col-6 ">
              <div className="field">
                <label className="block mb-2" htmlFor="eposta">
                  E-posta
                </label>
                <InputText
                  id="eposta"
                  className="w-full"
                  value={formData.eposta}
                  onChange={(e) =>
                    setFormData({ ...formData, eposta: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="field">
                <label className="block mb-2" htmlFor="instagram">
                  Instagram
                </label>
                <InputText
                  id="instagram"
                  className="w-full"
                  value={formData.instagram}
                  onChange={(e) =>
                    setFormData({ ...formData, instagram: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="field">
                <label className="block mb-2" htmlFor="twitter">
                  Twitter
                </label>
                <InputText
                  id="twitter"
                  className="w-full"
                  value={formData.twitter}
                  onChange={(e) =>
                    setFormData({ ...formData, twitter: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="field">
                <label className="block mb-2" htmlFor="youtube">
                  Youtube
                </label>
                <InputText
                  id="youtube"
                  className="w-full"
                  value={formData.youtube}
                  onChange={(e) =>
                    setFormData({ ...formData, youtube: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="field">
                <label className="block mb-2" htmlFor="linkedin">
                  LinkedIn
                </label>
                <InputText
                  id="linkedin"
                  className="w-full"
                  value={formData.linkedin}
                  onChange={(e) =>
                    setFormData({ ...formData, linkedin: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="field">
                <label className="block mb-2" htmlFor="facebook">
                  Facebook
                </label>
                <InputText
                  id="facebook"
                  className="w-full"
                  value={formData.facebook}
                  onChange={(e) =>
                    setFormData({ ...formData, facebook: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="col-12  md:col-6">
              <div className="field ">
                <label className=" block mb-2" htmlFor="adres">
                  Adres
                </label>
                <InputTextarea
                  id="adres"
                  className="w-full"
                  value={formData.adres}
                  onChange={(e) =>
                    setFormData({ ...formData, adres: e.target.value })
                  }
                  rows={4}
                />
              </div>
            </div>
            <div className="col-12   md:col-6">
              <div className="field ">
                <label className="block mb-2" htmlFor="haritaUrl">
                  Harita URL
                </label>
                <InputTextarea
                  id="haritaUrl"
                  className="w-full"
                  value={formData.haritaUrl}
                  onChange={(e) =>
                    setFormData({ ...formData, haritaUrl: e.target.value })
                  }
                  rows={4}
                />
              </div>
            </div>
          </div>
        </Dialog>
      </div>

      <div className="mt-5">
        <h2>Gelen Mesajlar</h2>
        <DataTable
          value={mesajlar}
          paginator
          rows={10}
          dataKey="id"
          loading={loading}
          emptyMessage="Mesaj bulunamadı"
          sortField="createdDate"
          sortOrder={-1}
        >
          <Column field="adSoyad" header="Ad Soyad" sortable />
          <Column field="telefonNo" header="Telefon" sortable />
          <Column
            field="baslik"
            header="Başlık"
            style={{ width: "20%" }}
            sortable
          />
          <Column
            field="aciklama"
            header="Mesaj"
            style={{ width: "30%" }}
            body={(rowData) => (
              <div
                className="cursor-pointer"
                style={{ whiteSpace: "pre-line" }}
                onClick={() => {
                  showDetailPopup(rowData);
                }}
              >
                {rowData.aciklama.length > 100 ? (
                  <div>{rowData.aciklama.substring(0, 100)}</div>
                ) : (
                  rowData.aciklama
                )}
                <div className="cursor-pointer" style={{ color: "#ed145b" }}>
                  Detaya Git &gt;
                </div>
              </div>
            )}
          />
          <Column
            field="createdDate"
            header="Tarih"
            sortable
            body={(rowData) =>
              new Date(rowData.createdDate).toLocaleDateString()
            }
          />
          <Column
            body={(rowData) => (
              <Button
                icon="pi pi-trash"
                rounded
                outlined
                severity="danger"
                onClick={() => confirmDelete(rowData)}
              />
            )}
            style={{ width: "5%" }}
          />
        </DataTable>
      </div>

      <DetailPopup
        isOpen={detailPopupVisible}
        onClose={() => setDetailPopupVisible(false)}
        data={selectedDetail}
        header="Mesaj Detayı"
      />
    </div>
  );
};

export default IletisimYonetimi;
